import { Avatar as AvatarPrimitive } from '@base-ui/react/avatar';
import * as React from 'react';

import { useImageRetry } from '../../lib/image-retry';
import { useSkeletonPulse } from '../../lib/skeleton-pulse';
import { cn } from '../../lib/utils';

/**
 * 这一格此刻**在等一张图**吗。
 *
 * 头像只有三种形态——**图**、**等图的骨架**、**图确定取不回来时的那枚兜底圆**——而它们**同一时刻
 * 只画一个**：`AvatarImage` 与 `AvatarFallback` 是同级，谁也不能靠「叠一层盖住谁」来表达互斥，
 * 因为盖住的那层只要有半点透明（`animate-pulse` 调的正是透明度）底下那枚饱和主色就会透上来
 * ——实测左上角账户入口看着是「一片脉动的粉」。所以「在不在等」由**只有它知道 URL 与重取轮次**的
 * `AvatarImage` 说了算，挂在 `Avatar` 上给同级的 `AvatarFallback` 读，后者据此决定这一格画骨架
 * 还是画结论。
 */
const AvatarPursuitContext = React.createContext<{
  awaiting: boolean;
  report: (awaiting: boolean) => void;
}>({ awaiting: false, report: () => {} });

/**
 * 绘制前跑完的 effect。上报「在不在等」必须用它（见 {@link AvatarImage}）：普通 `useEffect` 排在
 * 绘制之后，挂载那一帧屏上就会先出现兜底那枚主色圆，再被换成骨架——正是要消掉的那次闪。
 *
 * SSR 时降级成 `useEffect`（服务端没有布局，`useLayoutEffect` 会告警），此时首帧本就由客户端接手。
 */
const useIsoLayoutEffect =
  typeof window === 'undefined' ? React.useEffect : React.useLayoutEffect;

function Avatar({
  className,
  size = 'default',
  ...props
}: AvatarPrimitive.Root.Props & {
  size?: 'default' | 'sm' | 'lg';
}) {
  const [awaiting, setAwaiting] = React.useState(false);
  const pursuit = React.useMemo(
    () => ({ awaiting, report: setAwaiting }),
    [awaiting],
  );
  return (
    <AvatarPursuitContext.Provider value={pursuit}>
      <AvatarPrimitive.Root
        data-slot="avatar"
        data-size={size}
        className={cn(
          'group/avatar relative flex size-8 shrink-0 rounded-full select-none after:absolute after:inset-0 after:rounded-full after:border after:border-border after:mix-blend-darken data-[size=lg]:size-10 data-[size=sm]:size-6 dark:after:mix-blend-lighten',
          className,
        )}
        {...props}
      />
    </AvatarPursuitContext.Provider>
  );
}

/**
 * 头像图。**取图失败按瞬时故障处理，退避重取**（见 {@link useImageRetry}）；等图那段时间报一声
 * 「还在等」，让这一格画骨架而不是提前上色（见 {@link AvatarPursuitContext}）。
 *
 * 底座（Base UI）用 `new Image()` 探一次结果，失败即把 `Avatar.Root` 翻到 `Avatar.Fallback` 并
 * 就此定格——那张探测只随 `src` 重跑。于是断网几秒、或启动那一阵一批头像同时取被挤掉个别请求，
 * 这一枚就永久停在兜底态，直到调用方换 URL 或整棵子树重挂（虚拟列表里的行可能一直不重挂）。
 * 换 `key` 即换一枚新底座实例，探测随之重跑。重取期间 `Avatar.Fallback` 一直挂着（底座只在
 * `loaded` 时收它），所以两次尝试之间不会闪。
 *
 * 「还在等」走 **layout effect** 上报：它在浏览器绘制**之前**跑完并同步刷新，所以挂载那一帧、以及
 * 换 URL 那一帧，屏上都不会先闪一下兜底那枚主色圆。图显形与骨架退场同样是同一次提交——底座那
 * 张探测也在 layout effect 里（`useImageLoadingStatus`），命中缓存时首帧直接判 `loaded`，骨架
 * 压根不画，也不存在「骨架已撤、图还没到」的空洞。
 *
 * 轮次用完就停手，并照旧把 `onLoadingStatusChange` 透传给调用方——「这张图取不回来」要记进
 * 日志的话，由拿得到日志管线的那一层去做（本包不读 `window` 全局，见 15 的注入口径）。
 */
function AvatarImage({
  className,
  onLoadingStatusChange,
  ...props
}: AvatarPrimitive.Image.Props) {
  const src = typeof props.src === 'string' ? props.src : '';
  const retry = useImageRetry(src);
  const [loadedSrc, setLoadedSrc] = React.useState('');
  const { report } = React.useContext(AvatarPursuitContext);
  // 这张图**还在路上**：URL 有了、还没解码出来，重取轮次也没用尽。就绪钉在具体 URL 上，换图时
  // 上一张的结论跟着作废。
  const awaiting = !!src && loadedSrc !== src && !retry.failed;
  useIsoLayoutEffect(() => {
    report(awaiting);
    // 卸载 / 换图时把「在等」收回去：这一枚不在了，兜底层不该继续替它挂骨架。
    return () => {
      report(false);
    };
  }, [awaiting, report]);
  return (
    <AvatarPrimitive.Image
      key={retry.imageKey}
      data-slot="avatar-image"
      className={cn(
        'aspect-square size-full rounded-full object-cover',
        className,
      )}
      onLoadingStatusChange={(status) => {
        onLoadingStatusChange?.(status);
        if (status === 'loaded') {
          setLoadedSrc(src);
        }
        if (status === 'error') {
          retry.retryAfterError();
        }
      }}
      {...props}
    />
  );
}

/**
 * 图不在屏上时这一格画什么。它按 {@link AvatarPursuitContext} 分成两副面孔，**同一时刻只有一副**：
 *
 * - **还在等图**（有 URL、还没解码出来、重取轮次没用尽）：不起眼的中性灰骨架，等久了脉动（见
 *   {@link useSkeletonPulse}）。调用方传的 `backgroundColor` 这段时间**不画**——不是被盖住，是压根
 *   不上色。
 * - **图确定不来了**（没挂 `AvatarImage`、没给 `src`、或这个 URL 重取轮次用尽）：调用方那枚
 *   `backgroundColor`（人像 / 群像传那张头像自己的主色），没传就还是那档中性灰。
 *
 * 为什么主色只留给后者：一枚饱和满圆看着像「头像已经画好了」，于是慢头像看着不像「这张图还在路
 * 上」，而像「这人的头像就是一枚纯色圆」；更要紧的是**等待与失败在界面上会长得一模一样**，谁也分
 * 不出这一枚是在等还是已经没戏了（实测：用户刚换了头像，屏上那枚是「旧头像 → 一枚饱和色圆 →
 * 新头像」闪一下）。
 */
function AvatarFallback({
  className,
  style,
  ...props
}: AvatarPrimitive.Fallback.Props) {
  const { awaiting } = React.useContext(AvatarPursuitContext);
  const pulse = useSkeletonPulse(awaiting);
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      data-awaiting={awaiting || undefined}
      className={cn(
        'flex size-full items-center justify-center rounded-full bg-muted text-sm text-muted-foreground group-data-[size=sm]/avatar:text-xs',
        pulse && 'animate-pulse',
        className,
      )}
      // 等图那段把调用方那枚底色摘掉，只留 `bg-muted` 的骨架。**不能靠再叠一层盖住它**：
      // `animate-pulse` 调的是透明度，半透明的那半程会把主色透上来（实测「一片脉动的粉」）。
      style={awaiting ? { ...style, backgroundColor: undefined } : style}
      {...props}
    />
  );
}

function AvatarBadge({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="avatar-badge"
      className={cn(
        'absolute right-0 bottom-0 z-10 inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground bg-blend-color ring-2 ring-background select-none',
        'group-data-[size=sm]/avatar:size-2 group-data-[size=sm]/avatar:[&>svg]:hidden',
        'group-data-[size=default]/avatar:size-2.5 group-data-[size=default]/avatar:[&>svg]:size-2',
        'group-data-[size=lg]/avatar:size-3 group-data-[size=lg]/avatar:[&>svg]:size-2',
        className,
      )}
      {...props}
    />
  );
}

function AvatarGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="avatar-group"
      className={cn(
        'group/avatar-group flex -space-x-2 *:data-[slot=avatar]:ring-2 *:data-[slot=avatar]:ring-background',
        className,
      )}
      {...props}
    />
  );
}

function AvatarGroupCount({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="avatar-group-count"
      className={cn(
        'relative flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm text-muted-foreground ring-2 ring-background group-has-data-[size=lg]/avatar-group:size-10 group-has-data-[size=sm]/avatar-group:size-6 [&>svg]:size-4 group-has-data-[size=lg]/avatar-group:[&>svg]:size-5 group-has-data-[size=sm]/avatar-group:[&>svg]:size-3',
        className,
      )}
      {...props}
    />
  );
}

export {
  Avatar,
  AvatarBadge,
  AvatarFallback,
  AvatarGroup,
  AvatarGroupCount,
  AvatarImage,
};
